'use client';

import { Box, Button, HStack, Link, Text } from '@chakra-ui/react';
import { ExternalLink, GitFork, Slack } from 'lucide-react';
import type { SlackResearchOrigin } from '@/lib/slack-agent/types';

export function SlackConversationBanner({
  origin,
  onPrivateFork,
  onStop,
  busy,
  deliveryStatus,
  progress = [],
}: {
  origin: SlackResearchOrigin;
  onPrivateFork: () => void;
  busy: boolean;
  deliveryStatus?: string;
  progress?: Array<{ title: string; status: string }>;
  onStop: () => void;
}) {
  const destination =
    origin.channelType === 'im'
      ? 'Private Slack DM'
      : origin.channelName
        ? `#${origin.channelName}`
        : 'Slack conversation';
  return (
    <HStack
      px="4"
      py="2"
      gap="3"
      borderBottomWidth="1px"
      borderColor="ui.border.subtle"
      flexWrap="wrap"
    >
      <Slack size={16} aria-hidden="true" />
      <Box flex="1" minW="180px">
        <Text fontSize="sm" fontWeight="600">
          {destination}
        </Text>
        <Text fontSize="xs" color="ui.text.secondary">
          Replies and requested files sync to this conversation. Research can use accessible
          projects in this workspace.
        </Text>
        {deliveryStatus && (
          <Text
            fontSize="xs"
            role="status"
            color={
              ['reconciliation_required', 'failed'].includes(deliveryStatus)
                ? 'status.danger.fg'
                : 'ui.text.secondary'
            }
          >
            {deliveryStatus === 'streaming'
              ? 'Attune is working in Slack…'
              : deliveryStatus === 'delivered'
                ? 'Delivered to Slack'
                : deliveryStatus === 'cancelled'
                  ? 'Stopped'
                  : deliveryStatus === 'failed'
                    ? 'This turn failed. Recorded receipts were retained; send a new message to continue.'
                    : deliveryStatus === 'reconciliation_required'
                      ? 'Slack delivery is being reconciled. Do not resend this action.'
                      : `Slack sync: ${deliveryStatus}`}
          </Text>
        )}
        {progress.map((task, index) => (
          <Text key={index} fontSize="xs" color="ui.text.secondary">
            {task.status === 'complete' ? '✓' : task.status === 'error' ? '!' : '…'} {task.title}
          </Text>
        ))}
      </Box>
      {origin.deepLink && (
        <Link href={origin.deepLink} target="_blank" rel="noopener noreferrer" fontSize="sm">
          Open Slack <ExternalLink size={13} />
        </Link>
      )}
      <Button size="xs" variant="outline" onClick={onPrivateFork} disabled={busy}>
        <GitFork size={13} /> Private fork
      </Button>
      {deliveryStatus === 'streaming' && (
        <Button size="xs" variant="outline" onClick={onStop}>
          Stop
        </Button>
      )}
    </HStack>
  );
}
